/**
 *
 * @depends $window
 * @depends $q
 * @depends $sce
 * @depends manager.api.SettingsApi
 * @depends manager.api.PermissionsApi
 * @depends manager.api.AccountSettingsApi
 * @depends buildium.common.Locale
 * @depends buildium.common.ACCESS_LEVEL
 *
 */

import cloneDeep from 'lodash/fp/cloneDeep';
import {merge, includes} from 'lodash';
import {prepareHeapAnalytics} from '../../src/shared/helpers/tracking/heap';

export function SettingsService(
    $window,
    $q,
    $sce,
    $log,
    SettingsApi,
    PermissionsApi,
    AccountApi,
    AccountSettingsApi,
    AuthenticationApi,
    PropertiesApi,
    Locale,
    ACCESS_LEVEL,
    SYSTEM_ROLE,
    BUILDIUM_USER,
    SUBSCRIPTION_PLAN_TYPE,
    IS_PUBLIC_MODULE,
    IS_PRODUCTION,
    MobileBannerService,
    GlobalizeLoaderService
) {
    const service = this;
    let settings = null,
        permissions = null,
        jointPromise;

    function setApplicationState(data) {
        if (typeof data === 'string') {
            return null;
        }

        const newData = cloneDeep(data);

        if (!$window.bdApplicationState) {
            $window.bdApplicationState = {
                settings: newData
            };
        } else {
            $window.bdApplicationState.settings = data;
        }

        newData.CountryList.splice(0, 1);
        newData.StatesList.unshift({
            CountryId: service.USCountryId,
            Abbreviation: 'State',
            Id: ''
        });
        newData.StatesList.unshift({
            CountryId: service.CanadaCountryId,
            Abbreviation: 'Prov',
            Id: ''
        });

        //exports.ACHDirectSettingsInfo.ACHDirectCCReturnUrl = exports.ACHDirectSettingsInfo.ACHDirectCCReturnUrl.replace('{0}', $location.protocol() + '://' + $location.host());
        newData.SubscriberSettingsInfo.CountryId = parseInt(newData.SubscriberSettingsInfo.CountryId, 10);
        newData.SubscriberSettingsInfo.CompanyAddress = $sce.trustAsHtml(newData.SubscriberSettingsInfo.CompanyAddress);
        newData.PaymentSettingsInfo.EFTConvenienceFee = 7;

        GlobalizeLoaderService.loadGlobalizeScript(newData.SubscriberSettingsInfo.Culture);
        Locale.set(newData.SubscriberSettingsInfo.Culture);

        service.IsAdminUser = newData.UserSettingsInfo.RoleId === SYSTEM_ROLE.Administrator;
        service.IsReadOnlyAdminUser = newData.UserSettingsInfo.RoleId === SYSTEM_ROLE.AdministratorReadOnly;

        service.IsBuildiumAdminUser = newData.UserSettingsInfo.UserId === BUILDIUM_USER.AnonymousUser
            || newData.UserSettingsInfo.UserId === BUILDIUM_USER.BuildiumCustomerServiceUser || newData.UserSettingsInfo.UserId === BUILDIUM_USER.BatchProcessUser;

        service.IsBuildiumAdminUserWithWriteAccess = service.IsBuildiumAdminUser && service.IsAdminUser;

        service.IsReadOnlyBuildiumAdminUser = service.IsBuildiumAdminUser && service.IsReadOnlyAdminUser;

        service.ShowingsCoordinatorSettings = newData.ShowingsCoordinatorSettings;

        service.IsCustomerOnNewSubscriptionPlan = newData.SubscriberSettingsInfo.CurrentSubscriptionPlanType >= SUBSCRIPTION_PLAN_TYPE.Essential2020;

        service.IsTrialAccount = newData.SubscriberSettingsInfo.CurrentSubscriptionPlanType === SUBSCRIPTION_PLAN_TYPE.Trial;

        if ($window.bdApplicationState.settings) {
            AccountApi.getAnalyticsData().then((results) => {
                const subscriberSettings = $window.bdApplicationState.settings.SubscriberSettingsInfo;
                if (!service.IsBuildiumAdminUser) {
                    $window.buildium = {
                        analytics: {
                            tracking: {
                                account: {
                                    id: subscriberSettings.AccountId,
                                    type: results.AccountState,
                                    unitCeiling: results.AccountUnitCeiling,
                                    unitsAtTrialSignup: results.UnitsAtTrialSignup,
                                    subscriptionPlan: results.SubscriptionPlan,
                                    portfolioType: results.PortfolioType
                                },
                                user: {
                                    guid: results.UserGuid,
                                    id: $window.bdApplicationState.settings.UserSettingsInfo.UserId
                                }
                            }
                        }
                    };
                }

                prepareHeapAnalytics(results, $window.bdApplicationState.settings.AnalyticsSettingsInfo);
            }).catch(() => { });

            MobileBannerService.setMobileBanner($window.bdApplicationState.settings.UserSettingsInfo);
        }

        merge(service, newData);
        return service;
    }

    service.get = function get() {
        if (IS_PUBLIC_MODULE) {
            return $q.defer().promise;
        }
        if (!jointPromise) {
            if ($window.bdApplicationState) {
                setApplicationState($window.bdApplicationState.settings);
                service.permissions = $window.bdApplicationState.permissions;
                settings = $q.when(service);
                permissions = $q.when(service.permissions);
            } else {
                settings = SettingsApi.get().then(setApplicationState);
                permissions = PermissionsApi.get().then(function(data) {
                    service.permissions = data;

                    if (!$window.bdApplicationState) {
                        $window.bdApplicationState = {
                            permissions: data
                        };
                    } else {
                        $window.bdApplicationState.permissions = data;
                    }
                    return data;
                });
            }

            jointPromise = $q.all([settings, permissions]);
        }
        return jointPromise;
    };

    /**
     * @param {number} permissionConstant - a numeric value from the set PERMISSION enumerated list
     *
     * @returns {number} accessLevel - numeric value within the ACCESS_LEVEL enumerated list
     */
    service.getRoleACLForFunction = function getRoleACLForFunction(permissionConstant) {
        let accessLevel = ACCESS_LEVEL.None,
            permission = null;

        if (service.permissions && service.permissions.length) {
            permission = service.permissions.filter(function(perm) {
                return perm.Permission === permissionConstant;
            })[0];

            if (permission) {
                accessLevel = permission.AccessLevel;
            }
        }

        return accessLevel;
    };

    service.isAdminUser = function isAdminUser() {
        return service.IsAdminUser;
    };

    service.hasUserType = function hasUserType(userType) {
        return service.get().then(allSettings => {
            const userSettings = allSettings[0];

            return includes(userSettings.UserSettingsInfo.UserTypes, userType);
        });
    };

    service.getPostDaysInAdvance = function getPostDaysInAdvance() {
        return AccountSettingsApi.getPostDaysInAdvance()
            .then(function setPostingDaysInAdvance(response) {
                return response.value;
            });
    };

    service.mapPlanTypeToDisplayName = function mapPlanTypeToDisplayName(planType) {
        return {
            [SUBSCRIPTION_PLAN_TYPE.Trial]: 'Free Trial',
            [SUBSCRIPTION_PLAN_TYPE.Core2016]: 'Buildium Core',
            [SUBSCRIPTION_PLAN_TYPE.Pro2016]: 'Buildium Pro',
            [SUBSCRIPTION_PLAN_TYPE.Essential2020]: 'Buildium Essential',
            [SUBSCRIPTION_PLAN_TYPE.Growth2020]: 'Buildium Growth',
            [SUBSCRIPTION_PLAN_TYPE.Premium2020]: 'Buildium Premium'
        }[planType] || 'Buildium Plan';
    };

    service.getPlanDisplayText = function getPlanDisplayText() {
        return AccountApi.getSubscriptionPlanType()
            .then(function(response) {
                const planType = response.SubscriptionPlanType;
                return service.mapPlanTypeToDisplayName(planType);
            });
    };

    service.getCurrentUsersName = function getCurrentUsersName() {
        return AuthenticationApi.getCurrentUserInfo()
            .then(function(response) {
                return `${response.FirstName || ''} ${response.LastName || ''}`.trim() || response.LegalName; // fall back on LegalName
            });
    };

    service.getSubscriptionStats = function getSubscriptionStats() {
        return PropertiesApi.getSubscriptionCounts()
            .then(function(response) {
                const unusedUnits = response.maxActiveUnits - response.numActiveUnits;
                return `${response.maxActiveUnits} unit plan, ${unusedUnits} unused`;
            });
    };

    service.isAccountOnUpgradedSubscription = function isAccountOnUpgradedSubscription(subscriptionPlan) {
        return subscriptionPlan === SUBSCRIPTION_PLAN_TYPE.Pro2016
                || subscriptionPlan === SUBSCRIPTION_PLAN_TYPE.Premium2020;
    };

    return service;
}
SettingsService.$inject = [
    '$window',
    '$q',
    '$sce',
    '$log',
    'SettingsApi',
    'PermissionsApi',
    'AccountApi',
    'AccountSettingsApi',
    'AuthenticationApi',
    'PropertiesApi',
    'Locale',
    'ACCESS_LEVEL',
    'SYSTEM_ROLE',
    'BUILDIUM_USER',
    'SUBSCRIPTION_PLAN_TYPE',
    'IS_PUBLIC_MODULE',
    'IS_PRODUCTION',
    'MobileBannerService',
    'GlobalizeLoaderService'
];
